import React, { forwardRef, useEffect, useState } from "react";
import { GlobalNavigation } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { useLocation } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import { RootState } from "../../../../../../models";
import { RetailerProfile } from "../../../../../../models/idpConfig";
import { isRunningOnClientSide } from "../../../../../../shared/util";
import { useAppearance } from "../../../../app/hooks/useAppearance";
import { getEnvironment, isCheckout as isCheckoutSelector } from "../../../../app/state/selectors";
import { isAuthenticated } from "../../../../authentication/state/actions";
import { isAuthenticated as isAuthenticatedSelector } from "../../../../authentication/state/selectors";
import { useViewport } from "../../../../checkout/hooks";
import { toggleCartExpanded } from "../../../../checkout/state/actions/cartActions";
import {
    getNumberOfItemsInCart,
    isCartExpanded as isCartExpandedSelector,
} from "../../../../checkout/state/selectors/cartSelectors";
import { shouldOnlyShowSlideoutCart as shouldOnlyShowSlideoutCartSelector } from "../../../../checkout/state/selectors/combinedSelectors";
import "./ConnectedPageHeader.less";
import { getIsD2DOrTMDealer } from "../../../../retailer/state/selectors/selectors";
import { useGetRetailerSession } from "../../../../retailer/hooks/useGetRetailerSession";
import { API_URL_RETAILER_LOGOUT } from "../../../../retailer/api";
import { shouldHideCartInMenu as showCartInMenu } from "../../../state/selectors";
import ConnectedShoppingCart from "../ConnectedShoppingCart/ConnectedShoppingCart";
import EmptyCart from "../ConnectedShoppingCart/EmptyCart";
import { getDesktopLinks } from "./linksDesktop";
import { getMobileLinks } from "./linksMobile";

interface StateProps {
    env?: string;
    isCartExpanded: boolean;
    isCheckout: boolean;
    isD2DOrTMDealer: boolean;
    numberOfCartItems: number;
    shouldHideCartInMenu: boolean;
    shouldOnlyShowSlideoutCart: boolean;
    userIsAuthenticated: boolean;
}

type ConnectedPageHeaderProps = { isRetailer?: boolean } & StateProps;

const RETAILER_WIDTH_THRESHOLD = 1232;

const getLoggedInUser = (isLoggedIn?: boolean, retailerProfile?: RetailerProfile) => {
    if (retailerProfile) {
        return {
            name: retailerProfile.given_name ?? "Profil",
            isRetailer: true,
        };
    }

    if (isLoggedIn) {
        return {
            name: "Profil",
        };
    }
};

const ConnectedPageHeader: React.FC<ConnectedPageHeaderProps> = ({
    userIsAuthenticated,
    numberOfCartItems,
    isCartExpanded,
    isCheckout,
    isD2DOrTMDealer,
    isRetailer = false,
    shouldOnlyShowSlideoutCart,
    shouldHideCartInMenu,
    env,
}) => {
    const dispatch = useDispatch();
    const { resolved: appearance, setPreference: onAppearanceChange } = useAppearance();
    const { isDesktopVersion, width } = useViewport();
    const desktopLinks = getDesktopLinks({ isRetailer, isD2DOrTMDealer });
    const mobileLinks = getMobileLinks({ isRetailer, isD2DOrTMDealer });
    const links = isDesktopVersion ? desktopLinks : mobileLinks;
    const [isClientSide, setIsClientSide] = useState(false);
    const { pathname } = useLocation();
    const retailerSession = useGetRetailerSession(env);
    const retailerProfile = retailerSession?.profile;
    useEffect(() => {
        setIsClientSide(isRunningOnClientSide());
    }, []);

    useEffect(() => {
        dispatch(isAuthenticated());
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    let shoppingCartNumberOfItems = numberOfCartItems;
    if ((isRetailer && shouldHideCartInMenu) || (isRetailer && width > RETAILER_WIDTH_THRESHOLD)) {
        shoppingCartNumberOfItems = 0;
    }

    const handleRetailerLogout = async () => {
        try {
            const res = await fetch(API_URL_RETAILER_LOGOUT, { method: "POST" });
            const { endSessionUrl } = await res.json();
            window.location.assign(endSessionUrl ?? "/");
        } catch {
            window.location.assign("/");
        }
    };

    return (
        <GlobalNavigation
            isCartOpen={isCartExpanded}
            isSimplified={isCheckout || (isRetailer && _isEmpty(retailerProfile))}
            onCartOpenChange={
                !isClientSide
                    ? undefined
                    : (): void => {
                          if (!isCheckout || shouldOnlyShowSlideoutCart || !isDesktopVersion) {
                              dispatch(toggleCartExpanded(!isCartExpanded));
                          } else {
                              dispatch(toggleCartExpanded(false));
                          }
                      }
            }
            appKey="web-shop"
            linkComponent={forwardRef(({ href, ...props }, ref) => {
                return <a href={href} ref={ref} {...props} />;
            })}
            onLogoutClick={retailerProfile ? handleRetailerLogout : () => window.location.assign("")}
            loggedInUser={getLoggedInUser(userIsAuthenticated, retailerProfile)}
            shoppingCart={numberOfCartItems > 0 ? <ConnectedShoppingCart isRetailer /> : <EmptyCart />}
            shoppingCartNumberOfItems={shoppingCartNumberOfItems}
            drawerSize="lg"
            customLinks={links}
            currentPath={isRetailer ? pathname : undefined}
            hideTopMenuList={isRetailer}
            appearance={appearance}
            onAppearanceChange={onAppearanceChange}
        />
    );
};

const mapStateTopProps: (state: RootState) => StateProps = (state) => ({
    numberOfCartItems: getNumberOfItemsInCart(state),
    isCartExpanded: isCartExpandedSelector(state),
    shouldHideCartInMenu: showCartInMenu(state),
    userIsAuthenticated: isAuthenticatedSelector(state),
    isCheckout: isCheckoutSelector(state),
    isD2DOrTMDealer: getIsD2DOrTMDealer(state),
    shouldOnlyShowSlideoutCart: shouldOnlyShowSlideoutCartSelector(state),
    env: getEnvironment(state),
});

export default connect(mapStateTopProps)(ConnectedPageHeader);
